/* eslint max-classes-per-file: "off" */

import axios from 'axios';
import * as Sentry from '@sentry/browser';

import db from '../../components/db';
import sync from '../compiled/sync';
import types, { parseProgressMarkers, serializeProgressMarkers } from './conversions';
import { BirthdayError, NotMyBirthday, WrongStoreBirthday } from './errors';
import { setUpdating } from '../../redux/actions';

const DataTypes = {
  DEVICE_INFO: 154522,
  TYPED_URL: 40781,
  BOOKMARK: 32904,
  PASSWORD: 45873,
  SESSION: 50119,
  PREFERENCE: 37702,
};

const ErrorTypes = {
  NOT_MY_BIRTHDAY: 2,
};

const UPDATE_INTERVAL = 60 * 1000;

let onNewData = () => {};

let hidden = null;
let visibilityChange = null;
if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
  hidden = 'hidden';
  visibilityChange = 'visibilitychange';
} else if (typeof document.msHidden !== 'undefined') {
  hidden = 'msHidden';
  visibilityChange = 'msvisibilitychange';
} else if (typeof document.webkitHidden !== 'undefined') {
  hidden = 'webkitHidden';
  visibilityChange = 'webkitvisibilitychange';
}
let timerId;
let fetchTimestamp;

class BookmarksFolder {
  constructor(id, title, folderType) {
    this.id = id;
    this.name = title;
    this.items = null;
    this.folders = [];
    this.parent = null;
    this.count = 0;
    this.encrypted = false;
    this.folder_type = folderType;
  }
}

export async function purgeLocalData(update = false) {
  try {
    localStorage.removeItem('storeBirthday');
    localStorage.removeItem('progressMarkers');
    localStorage.removeItem('dataWereInitiallyFetched');
    await Promise.all([
      db.tabs.clear(),
      db.device_windows.clear(),
      db.history.clear(),
      db.devices.clear(),
      db.preferences.clear(),
      db.passwords.clear(),
      db.bookmarks.clear(),
    ]);
    onNewData();
    /* eslint-disable no-use-before-define */
    if (update) {
      fetchUpdates(true);
    }
    /* eslint-enable no-use-before-define */
  } catch (err) {
    console.log(err);
  }
}

const fetchFolder = async (root) => {
  const res = new BookmarksFolder(root.id, root.title, root.folder_type);
  res.items = db.bookmarks.where({ parent: root.id, folder: 0 });
  const folders = await db.bookmarks.where({ parent: root.id, folder: 1 }).toArray();
  await Promise.all(folders.map(async (f) => {
    const folder = await fetchFolder(f);
    folder.parent = res;
    res.folders.push(folder);
  }));
  res.count = res.folders.reduce((acc, f) => acc + f.count, await res.items.count());
  return res;
};

const transformTabs = (windows, tabs) => {
  const wnd = tabs.reduce((res, t) => {
    if (!(t.windowId in res)) {
      res[t.windowId] = [];
    }
    res[t.windowId].push(t);
    return res;
  }, {});

  const assignTabs = (windowIds) => windowIds
    .map((id) => wnd[id])
    .filter((winTabs) => winTabs !== undefined)
    .flat();

  return windows
    .filter((w) => w.windowIds.length !== 0)
    .map((w) => ({ ...w, tabs: assignTabs(w.windowIds) }))
    .filter((w) => w.tabs.length !== 0);
};

class Data {
  constructor(data = []) {
    this.data = data;
    this.count = 0;
    this.encrypted = false;
  }
}

const notifyForData = async () => {
  if (localStorage.getItem('dataWereInitiallyFetched') !== 'true') {
    onNewData();
    return;
  }

  const history = new Data();
  const preferences = new Data();
  const passwords = new Data();
  const tabs = new Data();
  const devices = new Data({});
  const speeddials = new Data({});
  const bookmarks = new Data({});

  const [windows, tabsData, historyData, prefsCount, pwdCount] = await Promise.all([
    db.device_windows.toArray(),
    db.tabs.toArray(),
    db.history.orderBy('visited').reverse().toArray(),
    db.preferences.count(),
    db.passwords.count(),
  ]);

  tabs.encrypted = tabsData.length > 0 ? tabsData[0].encrypted : false;
  tabs.count = tabsData.length;
  if (!tabs.encrypted) {
    tabs.data = transformTabs(windows, tabsData);
    tabs.count = tabs.data.reduce((acc, w) => acc + w.tabs.length, 0);
  }

  history.encrypted = historyData.length > 0 ? historyData[0].encrypted : false;
  history.count = historyData.length;
  function isNewDate(h, i) {
    if (i === 0) return true;

    const first = h.visited;
    const second = historyData[i - 1].visited;
    return first.getFullYear() !== second.getFullYear()
      || first.getMonth() !== second.getMonth()
      || first.getDate() !== second.getDate();
  }
  if (!history.encrypted) {
    let cur;
    historyData.map((h, i) => {
      if (isNewDate(h, i)) {
        cur = [];
        history.data.push(cur);
      }
      cur.push(h);
      return h;
    });
  }

  preferences.count = prefsCount;

  passwords.count = pwdCount;
  passwords.encrypted = true;

  await db.devices.each((d) => { devices.data[d.name + d.platform] = d; });
  devices.data = Object.values(devices.data);
  devices.count = devices.data.length;

  const bmEncrypted = Boolean((await db.bookmarks.limit(1).first() || {}).encrypted);
  const sdRoot = await db.bookmarks.get({ type: 'speeddial_bookmarks' });
  if (sdRoot) {
    const sdTree = await fetchFolder(sdRoot);
    speeddials.data = sdTree;
    speeddials.count = sdTree.count;
  }
  speeddials.encrypted = bmEncrypted;

  let roots = await db.bookmarks.where('type').notEqual('').toArray();
  roots = roots.filter((r) => r.type !== 'speeddial_bookmarks');
  const bmTree = new BookmarksFolder('-1');
  bmTree.folders = await Promise.all(roots.map(fetchFolder));
  bmTree.folders.forEach((f) => { f.parent = bmTree; });
  bmTree.count = bmTree.folders.reduce((acc, f) => acc + f.count, 0);
  bookmarks.count = bmTree.count;
  bookmarks.data = bmTree;
  bookmarks.encrypted = bmEncrypted;

  onNewData({
    tabs,
    devices,
    history,
    preferences,
    passwords,
    speeddials,
    bookmarks,
  });
};

const getUpdates = async (initialUpdate) => {
  const storeBirthday = localStorage.getItem('storeBirthday');
  const storedMarkers = localStorage.getItem('progressMarkers');
  let progressMarkers;
  if (storedMarkers) {
    progressMarkers = parseProgressMarkers(storedMarkers);
  } else {
    progressMarkers = Object.values(DataTypes).reduce(
      (map, t) => { map[t] = ''; return map; },
      {},
    );
  }

  const msg = {
    messageContents: sync.sync_pb.ClientToServerMessage.Contents.GET_UPDATES,
    getUpdates: {
      fromProgressMarker: Object.keys(progressMarkers).map(
        (t) => ({ dataTypeId: parseInt(t, 10), token: progressMarkers[t] }),
      ),
      getUpdatesOrigin: initialUpdate
        ? sync.sync_pb.SyncEnums.GetUpdatesOrigin.NEW_CLIENT
        : sync.sync_pb.SyncEnums.GetUpdatesOrigin.PERIODIC,
    },
    storeBirthday,
  };

  const msgProto = sync.sync_pb.ClientToServerMessage.create(msg);
  const msgData = new Uint8Array(sync.sync_pb.ClientToServerMessage.encode(msgProto).finish());

  const response = await axios({
    method: 'post',
    url: new URL('/ui_backend/proxy/sync/api/sync/command/', process.env.REACT_APP_PROXY_HOST),
    data: msgData.buffer,
    headers: {
      'Content-type': 'application/octet-stream',
      Accept: 'application/octet-stream',
    },
    responseType: 'arraybuffer',
    withCredentials: true,
  });

  const data = sync.sync_pb.ClientToServerResponse.decode(
    new Uint8Array(response.data),
  );

  if (data.error.ErrorType === ErrorTypes.NOT_MY_BIRTHDAY) {
    throw new NotMyBirthday();
  }

  if (storeBirthday !== null && storeBirthday !== data.storeBirthday) {
    throw new WrongStoreBirthday();
  }

  const newProgressMarkers = {};
  data.getUpdates.newProgressMarker.forEach((m) => {
    newProgressMarkers[m.dataTypeId] = m.token;
  });

  localStorage.setItem('progressMarkers', serializeProgressMarkers(newProgressMarkers));
  localStorage.setItem('storeBirthday', data.storeBirthday);
  localStorage.setItem('dataWereInitiallyFetched', 'true');

  return data;
};

const parseEntries = (entries) => entries.reduce((parsed, e) => {
  if (e.deleted) {
    if (types.isRemovedTab(e)) {
      parsed.tombstones.sessions.push(e.idString);
    } else if (types.isRemovedHistory(e)) {
      parsed.tombstones.history.push(e.idString);
    } else if (types.isRemovedDeviceInfo(e)) {
      parsed.tombstones.devices.push(e.idString);
    } else if (types.isRemovedPreference(e)) {
      parsed.tombstones.preference.push(e.idString);
    } else if (types.isRemovedPassword(e)) {
      parsed.tombstones.passwords.push(e.idString);
    } else if (types.isRemovedBookmark(e)) {
      parsed.tombstones.bookmarks.push(e.idString);
    }
  } else if (types.isWindow(e)) {
    parsed.windows.push(types.parseWindow(e));
  } else if (types.isTab(e)) {
    parsed.tabs.push(types.parseTab(e));
  } else if (types.isHistory(e)) {
    parsed.history.push(types.parseHistory(e));
  } else if (types.isDeviceInfo(e)) {
    parsed.devices.push(types.parseDeviceInfo(e));
  } else if (types.isPreference(e)) {
    parsed.preferences.push(types.parsePreference(e));
  } else if (types.isPassword(e)) {
    parsed.passwords.push(types.parsePassword(e));
  } else if (types.isBookmark(e)) {
    parsed.bookmarks.push(types.parseBookmark(e));
  }
  return parsed;
}, {
  windows: [],
  tabs: [],
  history: [],
  devices: [],
  preferences: [],
  passwords: [],
  bookmarks: [],
  tombstones: {
    sessions: [],
    history: [],
    devices: [],
    preferences: [],
    passwords: [],
    bookmarks: [],
  },
});

const squashHistory = (history) => {
  history.sort((l, r) => {
    if (l.visited.getTime() === r.visited.getTime()) {
      if (l.normal_url.length === r.normal_url.length) {
        return l.url.length - r.url.length;
      }
      return l.normal_url.length - r.normal_url.length;
    }
    return l.visited.getTime() - r.visited.getTime();
  });
  return history.reduce((acc, h, i) => {
    if (i === history.length - 1
        || h.normal_url !== history[i + 1].normal_url) {
      acc.push(h);
    }
    return acc;
  }, []);
};

const errorBoundary = (f) => (...args) => {
  let res;
  try {
    res = f(...args);
  } catch (e) {
    Sentry.captureException(e);
  }
  return res;
};

const fetchUpdates = errorBoundary(async (initialUpdate = false) => {
  setUpdating(true);
  let response;
  try {
    response = await getUpdates(!localStorage.getItem('storeBirthday') || initialUpdate);
  } catch (e) {
    if (e instanceof BirthdayError) {
      await purgeLocalData();
      notifyForData();
      setTimeout(fetchUpdates);
      return;
    }
    if (e.response && (e.response.status === 403 || e.response.status === 401)) {
      await purgeLocalData();
      window.open(new URL('/ui_backend/oauth2/login/', process.env.REACT_APP_PROXY_HOST), '_self');
      return;
    }
    setUpdating(false);
    throw e;
  }

  const parsedData = parseEntries(response.getUpdates.entries);
  if (parsedData.history.length > 0 && !parsedData.history[0].encrypted) {
    parsedData.history = squashHistory(parsedData.history);
  }

  await Promise.all([
    db.device_windows.bulkPut(parsedData.windows),
    db.tabs.bulkPut(parsedData.tabs),
    db.history.bulkPut(parsedData.history),
    db.devices.bulkPut(parsedData.devices),
    db.preferences.bulkPut(parsedData.preferences),
    db.passwords.bulkPut(parsedData.passwords),
    db.bookmarks.bulkPut(parsedData.bookmarks),
  ]);

  await Promise.all([
    db.device_windows.bulkDelete(parsedData.tombstones.sessions),
    db.tabs.bulkDelete(parsedData.tombstones.sessions),
    db.history.bulkDelete(parsedData.tombstones.history),
    db.devices.bulkDelete(parsedData.tombstones.devices),
    db.preferences.bulkDelete(parsedData.tombstones.preferences),
    db.passwords.bulkDelete(parsedData.tombstones.passwords),
    db.bookmarks.bulkDelete(parsedData.tombstones.bookmarks),
  ]);

  notifyForData();

  if (response.getUpdates.changesRemaining === 1) {
    setTimeout(fetchUpdates.bind(null, true));
  } else {
    fetchTimestamp = new Date();
    setUpdating(false);
    if (!document[hidden]) timerId = setTimeout(fetchUpdates, UPDATE_INTERVAL);
  }
});

const handleVisibilityChange = () => {
  if (document[hidden]) {
    clearTimeout(timerId);
  } else {
    let elapsed = UPDATE_INTERVAL + 1;
    if (fetchTimestamp) elapsed = new Date().getTime() - fetchTimestamp.getTime();
    if (elapsed > UPDATE_INTERVAL) {
      fetchUpdates();
    } else {
      timerId = setTimeout(fetchUpdates, UPDATE_INTERVAL - elapsed);
    }
  }
};

export const updateData = async (clb) => {
  onNewData = clb;
  document.addEventListener(visibilityChange, handleVisibilityChange, false);

  notifyForData();

  if (!document[hidden]) fetchUpdates();
};
